import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, CheckCircle2, Clock, ArrowUpRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useApp } from "@/context/AppContext";
import { DemoLinks } from "@/components/DemoLinks";
import { DemoStats } from "@/components/DemoStats";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PageHeader, Panel, Loading, Empty, Kpi, Money, Th, Td, TableWrap } from "@/components/kit";
import { dmy } from "@/lib/format";

const DeclCell = ({ p, t }) => {
  if (!p) return <span className="text-slate-300">—</span>;
  if (p.filed) return <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700"><CheckCircle2 className="h-3.5 w-3.5" />{t("decl_filed")}</span>;
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-semibold ${p.late ? "text-red-600" : "text-amber-700"}`}>
      {p.late ? <AlertTriangle className="h-3.5 w-3.5" /> : <Clock className="h-3.5 w-3.5" />}{p.late ? t("decl_late") : `${t("decl_before")} ${dmy(p.deadline)}`}
    </span>
  );
};

export default function FirmSpace() {
  const { api, t, setCompanyId, me } = useApp();
  const nav = useNavigate();
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/firm/overview").then((r) => setD(r.data)); }, [api]);
  const open = (c, path = "/app") => { setCompanyId(c.id); nav(path); };
  return (
    <>
      <PageHeader eyebrow={t("firm_eyebrow")} title={t("firm_space")} />
      {!d ? <Loading /> : (
        <>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
            <Kpi testid="firm-kpi-companies" label={t("companies")} value={d.totals.companies} accent="navy" />
            <Kpi testid="firm-kpi-receivable" label={t("receivables")} value={<Money v={d.totals.receivable} />} />
            <Kpi testid="firm-kpi-revenue" label={`${t("revenue")} · ${d.month}`} value={<Money v={d.totals.revenue_month} />} />
            <Kpi testid="firm-kpi-topay" label={`${t("total_to_pay")} · ${d.month}`} value={<Money v={d.totals.to_pay_month} />} accent="orange" />
            <Kpi testid="firm-kpi-pending" label={`${t("declarations")} ${d.previous_month}`} value={d.totals.pending_declarations} sub={`${t("st_rejected")} TTN : ${d.totals.rejected} · ${t("justificatifs")} : ${d.totals.justificatifs_new}`} accent="red" />
          </div>
          <Panel title={t("firm_portfolio")}>
            {d.companies.length === 0 ? <Empty /> : (
              <TableWrap>
                <thead className="border-b"><tr><Th>{t("company")}</Th><Th>{t("role")}</Th><Th className="text-end">{t("revenue")}</Th><Th className="text-end">{t("receivables")}</Th><Th className="text-end">{t("overdue")}</Th><Th className="text-end">{t("total_to_pay")}</Th><Th>{t("declarations")} {d.previous_month}</Th><Th>TTN</Th><Th>{t("justificatifs")}</Th><Th /></tr></thead>
                <tbody>{d.companies.map((c) => (
                  <tr key={c.id} className="border-b border-slate-50 hover:bg-teal-50/30" data-testid={`firm-row-${c.id}`}>
                    <Td><b>{c.name}</b><div className="num text-[11px] text-slate-500">{c.matricule_fiscal} · {c.plan_code} · {t(c.sub_state)}</div></Td>
                    <Td className="text-xs">{t(`r_${c.role}`)}</Td>
                    <Td className="text-end">{c.revenue_month !== undefined ? <Money v={c.revenue_month} /> : "—"}</Td>
                    <Td className="text-end">{c.receivable !== undefined ? <Money v={c.receivable} /> : "—"}</Td>
                    <Td className="text-end">{c.overdue_count ? <span className="text-red-600"><Money v={c.overdue_amount} /> ({c.overdue_count})</span> : "—"}</Td>
                    <Td className="text-end">{c.to_pay_month !== undefined ? <Money v={c.to_pay_month} /> : "—"}</Td>
                    <Td><DeclCell p={c.prev_declaration} t={t} /></Td>
                    <Td className="text-xs">{c.rejected ? <span className="font-semibold text-red-600">{c.rejected} {t("st_rejected").toLowerCase()}</span> : <span className="text-slate-400">OK</span>}{c.drafts ? <div className="text-slate-400">{c.drafts} {t("st_draft").toLowerCase()}</div> : null}</Td>
                    <Td>{c.justificatifs_new ? <button onClick={() => open(c, "/app/justificatifs")} className="rounded-full bg-orange-100 px-2 py-0.5 text-xs font-bold text-orange-800 hover:bg-orange-200" data-testid={`firm-justif-${c.id}`}>{c.justificatifs_new} {t("justif_new").toLowerCase()}</button> : <span className="text-xs text-slate-400">—</span>}</Td>
                    <Td className="text-end whitespace-nowrap">
                      {c.prev_declaration && <Button size="sm" variant="outline" onClick={() => open(c, "/app/declarations")} data-testid={`firm-open-decl-${c.id}`}>{t("declarations")}</Button>}
                      <Button size="sm" variant="ghost" onClick={() => open(c)} data-testid={`firm-open-${c.id}`}><ArrowUpRight className="h-4 w-4" /></Button>
                    </Td>
                  </tr>))}</tbody>
              </TableWrap>
            )}
          </Panel>
          <p className="text-xs text-slate-500">{t("firm_help")}</p>
          {me?.companies?.some((c) => c.role === "firm") && (
            <Tabs defaultValue="links" data-testid="firm-demo-tabs">
              <TabsList>
                <TabsTrigger value="links" data-testid="firm-tab-demo-links">{t("demo_tab_links")}</TabsTrigger>
                <TabsTrigger value="stats" data-testid="firm-tab-demo-stats">{t("demo_tab_stats")}</TabsTrigger>
              </TabsList>
              <TabsContent value="links"><DemoLinks /></TabsContent>
              <TabsContent value="stats"><DemoStats /></TabsContent>
            </Tabs>
          )}
        </>
      )}
    </>
  );
}
